import { fetchWidgetEvents } from "./api";
import { readWidgetOptions } from "./config";
import { renderCalendarUnavailable, renderError, renderLoading, renderMissingKey, renderPayload } from "./render";
import { injectStyles } from "./styles";

const WIDGET_SELECTOR = "[data-lm-events]";

export async function mountWidget(element: HTMLElement): Promise<void> {
  element.dataset.lmMounted = "true";

  injectStyles(element.ownerDocument);
  const options = readWidgetOptions(element);
  if (!options) {
    renderMissingKey(element);
    return;
  }

  if (options.view === "calendar") {
    renderCalendarUnavailable(element, options);
    return;
  }

  renderLoading(element, options);

  try {
    const payload = await fetchWidgetEvents(options);
    renderPayload(element, payload, options);
  } catch {
    renderError(element, options);
  }
}

export function init(root: ParentNode = document): void {
  const widgets = Array.from(root.querySelectorAll<HTMLElement>(WIDGET_SELECTOR));
  for (const widget of widgets) {
    if (widget.dataset.lmMounted !== "true") {
      widget.dataset.lmMounted = "true";
      void mountWidget(widget);
    }
  }
}

export function observeDynamicWidgets(root: Node = document.documentElement): MutationObserver | null {
  if (typeof MutationObserver === "undefined") {
    return null;
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof HTMLElement) {
          if (node.matches(WIDGET_SELECTOR) && node.dataset.lmMounted !== "true") {
            node.dataset.lmMounted = "true";
            void mountWidget(node);
          }
          const nested = node.querySelectorAll<HTMLElement>(`${WIDGET_SELECTOR}:not([data-lm-mounted="true"])`);
          for (const el of Array.from(nested)) {
            el.dataset.lmMounted = "true";
            void mountWidget(el);
          }
        }
      }
    }
  });

  observer.observe(root, { childList: true, subtree: true });
  return observer;
}
